import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidGen } from 'uuid';
import { getConfig } from '../api';

import {
  IDLE, PENDING, SUCCEEDED, FAILED,
} from '../constants/asyncLoadingStatuses';
import Logger from '../utils/logger';
import Cookies from '../providers/Cookies/Cookies';

const loadResyConfigThunk = createAsyncThunk(
  'resyConfig/loadResyConfig',
  async (params, { rejectWithValue }) => {
    try {
      const response = await getConfig(params);
      return response;
    } catch (error) {
      const message = error?.data?.message?.venue_id;
      const status = error?.status;
      const formattedError = { status, data: { message } };
      return rejectWithValue(formattedError);
    }
  }
);

const resyConfigSlice = createSlice({
  name: 'resyConfig',
  initialState: {
    configStatus: IDLE,
    venueConfigStatus: IDLE,
    config: null,
    venues: {},
    deviceId: Cookies.get('uuid') || uuidGen(),
  },
  extraReducers: (builder) => {
    builder.addCase(loadResyConfigThunk.pending, (state, action) => {
      const { meta: { arg } } = action;
      if (arg?.venue_id) {
        state.venueConfigStatus = PENDING;
      } else {
        state.configStatus = PENDING;
      }
    });
    builder.addCase(loadResyConfigThunk.fulfilled, (state, action) => {
      const { meta: { arg }, payload: data } = action;
      if (data.venue) {
        state.venues[arg.venue_id] = data;
        state.venueConfigStatus = SUCCEEDED;
      } else {
        state.config = data;
        state.configStatus = SUCCEEDED;
      }
    });
    builder.addCase(loadResyConfigThunk.rejected, (state, action) => {
      const { meta: { arg }, payload: { data: { message } } } = action;
      if (arg?.venue_id) {
        state.venueConfigStatus = FAILED;
      } else {
        state.configStatus = FAILED;
      }
      Logger.error(`[ERROR] loadResyConfigThunk, ${message}`);
    });
  },
});

// get*Thunk calls that return state are intended for legacy Angular controller use.
// For returning values from thunks, see:
// https://redux.js.org/usage/writing-logic-thunks#returning-values-from-thunks
const getResyConfigThunk = (prop) => (dispatch, getState) => {
  const { resyConfig: { config, configStatus } } = getState();

  if (config && prop) {
    if (prop in config) {
      return Promise.resolve(config[prop]);
    }
    Logger.error(`[ERROR] getResyConfigThunk: Property "${prop}" not found in config.`);
    return Promise.resolve(null);
  }

  if (config) {
    return Promise.resolve(config);
  }

  if (configStatus !== PENDING) {
    return dispatch(loadResyConfigThunk())
      .unwrap()
      .then((payload) => Promise.resolve(payload))
      .catch(() => Promise.resolve(null));
  }
  return Promise.resolve(null);
};

const getResyConfigVenueThunk = (id) => (dispatch, getState) => {
  const { resyConfig: { venues, venueConfigStatus } } = getState();
  if (venues[id]) {
    return Promise.resolve(venues[id]);
  }

  if (venueConfigStatus !== PENDING) {
    return dispatch(loadResyConfigThunk({ venue_id: id }))
      .unwrap()
      .then((payload) => {
        let response;
        if (payload?.venue) {
          response = payload;
        }
        return Promise.resolve(response);
      })
      .catch(() => Promise.resolve(null));
  }
  return Promise.resolve(null);
};

export {
  getResyConfigThunk,
  getResyConfigVenueThunk,
  loadResyConfigThunk,
};

export default resyConfigSlice.reducer;
